import React, { useEffect, useRef, useState } from "react";
import {
  Download, X, FileText, Image as ImageIcon, FileWarning,
  FileSpreadsheet, File, FileArchive, Loader2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fileSizeLabel, formatDateTime } from "../lib/constants";
import RevisionBadge from "./RevisionBadge";

const TEXT_MIMES = [
  "text/plain", "text/csv", "text/markdown", "text/html",
  "application/json", "application/xml", "text/xml",
];

const IMAGE_MIMES = [
  "image/png", "image/jpeg", "image/jpg", "image/webp",
  "image/gif", "image/bmp", "image/svg+xml",
];

function classify(doc) {
  const mime = (doc?.file_type || "").toLowerCase();
  const name = (doc?.file_name || "").toLowerCase();
  if (IMAGE_MIMES.includes(mime) || /\.(png|jpe?g|webp|gif|bmp|svg)$/.test(name)) return "image";
  if (mime === "application/pdf" || /\.pdf$/.test(name)) return "pdf";
  if (TEXT_MIMES.includes(mime) || /\.(txt|csv|md|json|xml|log)$/.test(name)) return "text";
  return "other";
}

function IconFor({ kind, size = 44 }) {
  switch (kind) {
    case "image": return <ImageIcon size={size} className="text-[color:var(--atech-copper)]" strokeWidth={1.25} />;
    case "pdf": return <FileText size={size} className="text-[color:var(--atech-copper)]" strokeWidth={1.25} />;
    case "text": return <FileText size={size} className="text-[color:var(--atech-copper)]" strokeWidth={1.25} />;
    default: return <File size={size} className="text-[color:var(--atech-text-dim)]" strokeWidth={1.25} />;
  }
}

/**
 * DocumentPreview — modal renderer for inline preview of images, PDFs, and
 * small text files. For office/CAD/zip formats we show a clear
 * "preview not available" card with metadata + a Download action so the
 * user isn't stuck in a dead end.
 */
export default function DocumentPreview({ doc, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const overlayRef = useRef(null);
  const kind = classify(doc);

  // Scroll overlay to top whenever a new document opens
  useEffect(() => {
    if (overlayRef.current) overlayRef.current.scrollTop = 0;
  }, [doc?.id]);

  // Keep onClose in a ref so we can reference it inside the effect without
  // adding it as a dependency (which would restart the effect on every parent render).
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    let url = null;
    let cancelled = false;

    const load = async () => {
      if (!doc?.storage_path) return; // demo placeholder — no bytes
      if (kind === "other") return;   // don't fetch for unsupported formats
      setLoading(true); setErr("");
      try {
        const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
        if (cancelled) return;
        if (kind === "text") {
          const text = await resp.data.text();
          setTextContent(text.length > 200_000 ? text.slice(0, 200_000) + "\n\n… [truncated]" : text);
        } else {
          url = URL.createObjectURL(resp.data);
          setBlobUrl(url);
        }
      } catch (e) {
        if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    // Close on Escape — always reads the latest onClose via the ref
    const onKey = (e) => { if (e.key === "Escape") onCloseRef.current(); };
    window.addEventListener("keydown", onKey);

    return () => {
      cancelled = true;
      window.removeEventListener("keydown", onKey);
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc?.id, kind]); // kind is derived from doc; changes whenever doc.id changes

  const download = async () => {
    try {
      const resp = blobUrl
        ? null
        : await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const src = blobUrl || URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = src; a.download = doc.file_name || "file"; a.click();
      if (!blobUrl) URL.revokeObjectURL(src);
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="document-preview"
    >
      <div
        className="card-surface w-full max-w-5xl mx-auto mt-6 mb-12 flex flex-col fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[color:var(--atech-border)]">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="font-display text-xl text-[color:var(--atech-text)] truncate">
                {doc.title}
              </h3>
              <RevisionBadge status={doc.document_status} revision={doc.revision} drawingNumber={doc.drawing_number} />
            </div>
            <div className="text-xs text-[color:var(--atech-text-muted)] flex flex-wrap items-center gap-3">
              <span className="font-mono truncate">{doc.file_name}</span>
              <span>· {fileSizeLabel(doc.file_size)}</span>
              <span>· {formatDateTime(doc.created_at)}</span>
              {doc.uploaded_by_name && <span>· by {doc.uploaded_by_name}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {doc.storage_path && (
              <button
                onClick={download}
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-border-strong)]"
                data-testid="preview-download"
              >
                <Download size={13} /> Download
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"
              aria-label="Close preview"
              data-testid="preview-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-[50vh] overflow-auto bg-[color:var(--atech-bg)]">
          {!doc.storage_path ? (
            <Placeholder
              icon={<FileWarning size={44} className="text-[color:var(--atech-text-dim)]" strokeWidth={1.25} />}
              title="Demo placeholder"
              body="This record doesn't have an uploaded file attached — it's part of the demo dataset. Upload a real file to enable preview."
            />
          ) : loading ? (
            <PreviewLoading />
          ) : err ? (
            <Placeholder icon={<FileWarning size={44} className="text-[#F87171]" strokeWidth={1.25} />} title="Preview failed" body={err} />
          ) : kind === "image" && blobUrl ? (
            <PreviewImage blobUrl={blobUrl} doc={doc} />
          ) : kind === "pdf" && blobUrl ? (
            <PreviewPdf blobUrl={blobUrl} doc={doc} />
          ) : kind === "text" && textContent !== null ? (
            <PreviewText textContent={textContent} />
          ) : (
            <UnsupportedPlaceholder doc={doc} onDownload={download} />
          )}
        </div>
      </div>
    </div>
  );
}

function Placeholder({ icon, title, body }) {
  return (
    <div className="h-[50vh] flex flex-col items-center justify-center text-center p-10 max-w-lg mx-auto">
      <div className="mb-5">{icon}</div>
      <div className="font-display text-xl text-[color:var(--atech-text)] mb-2">{title}</div>
      <p className="text-sm text-[color:var(--atech-text-dim)] leading-relaxed">{body}</p>
    </div>
  );
}

function PreviewLoading() {
  return (
    <div className="h-[50vh] flex items-center justify-center text-[color:var(--atech-text-dim)] gap-2">
      <Loader2 size={16} className="animate-spin" />
      <span className="label">Loading preview…</span>
    </div>
  );
}

function PreviewImage({ blobUrl, doc }) {
  return (
    <div className="w-full flex items-start justify-center p-6">
      <img src={blobUrl} alt={doc.title} className="max-w-full object-contain" data-testid="preview-image" />
    </div>
  );
}

function PreviewPdf({ blobUrl, doc }) {
  return (
    <iframe
      src={blobUrl}
      title={doc.title}
      className="w-full"
      style={{ minHeight: "80vh" }}
      data-testid="preview-pdf"
    />
  );
}

function PreviewText({ textContent }) {
  return (
    <pre
      className="text-xs font-mono leading-relaxed text-[color:var(--atech-text)] whitespace-pre-wrap break-words p-6 overflow-auto"
      data-testid="preview-text"
    >
      {textContent}
    </pre>
  );
}

function UnsupportedPlaceholder({ doc, onDownload }) {
  const name = (doc.file_name || "").toLowerCase();
  let Icon = File;
  let friendly = "file";
  if (/\.(docx?|rtf|odt)$/.test(name)) { Icon = FileText; friendly = "Word document"; }
  else if (/\.(xlsx?|ods|csv)$/.test(name)) { Icon = FileSpreadsheet; friendly = "spreadsheet"; }
  else if (/\.(zip|rar|7z|tar|gz)$/.test(name)) { Icon = FileArchive; friendly = "archive"; }
  else if (/\.(dwg|dxf|rvt|ifc|skp)$/.test(name)) { Icon = FileText; friendly = "CAD drawing"; }

  return (
    <div className="h-[50vh] flex flex-col items-center justify-center text-center p-10 max-w-lg mx-auto">
      <Icon size={52} className="text-[color:var(--atech-copper)] mb-5" strokeWidth={1.1} />
      <div className="font-display text-xl text-[color:var(--atech-text)] mb-2">
        Inline preview not available
      </div>
      <p className="text-sm text-[color:var(--atech-text-dim)] leading-relaxed mb-6">
        This {friendly} can't be previewed inside the browser. Images, PDFs, and
        plain-text documents preview automatically — everything else can be
        downloaded securely.
      </p>
      <button onClick={onDownload} className="btn-copper text-sm flex items-center gap-2" data-testid="preview-unsupported-download">
        <Download size={14} /> Download {doc.file_name}
      </button>
    </div>
  );
}

/** Small reusable "eye" button for document rows. */
export function PreviewButton({ onClick, testId }) {
  return (
    <button
      onClick={onClick}
      title="Preview"
      className="p-2 text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:bg-[color:var(--atech-surface-hover)] rounded-[2px]"
      data-testid={testId}
    >
      <EyeIcon />
    </button>
  );
}

// Local lucide eye replica to keep this component self-contained
function EyeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
